import React, { createContext, useContext, useEffect, useRef } from 'react'
import { useRealtimeStore, useRealtimeCleanup, type ConnectionConfig } from '@/shared/stores'
import { toast } from '@/lib/toast'

interface RealtimeContextValue {
  subscribe: (channel: string, callback: (data: unknown) => void) => () => void
  unsubscribe: (channel: string) => void
  sendMessage: (channel: string, type: string, payload: unknown) => void
  isOnline: boolean
  isConnected: boolean
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null)

interface RealtimeProviderProps {
  children: React.ReactNode
  connections?: Array<{ id: string; config: ConnectionConfig }>
  enableToasts?: boolean
  autoSync?: boolean
  syncInterval?: number
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({
  children,
  connections = [],
  enableToasts = true,
  autoSync = true,
  syncInterval = 30000
}) => {
  const store = useRealtimeStore()
  const cleanup = useRealtimeCleanup()
  const syncIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const lastStatusRef = useRef(store.globalStatus)

  // Initialize connections
  useEffect(() => {
    const initializeConnections = async () => {
      for (const { id, config } of connections) {
        try {
          await store.createConnection(id, config)
        } catch (error) {
          console.error(`Failed to create connection ${id}:`, error)
          if (enableToasts) {
            toast.error(`Failed to connect to ${id}`)
          }
        }
      }
    }

    initializeConnections()

    return cleanup
  }, [connections.length]) // Only re-run if number of connections changes

  // Handle status changes and show toasts
  useEffect(() => {
    if (!enableToasts) return

    const currentStatus = store.globalStatus
    const lastStatus = lastStatusRef.current

    if (currentStatus !== lastStatus) {
      switch (currentStatus) {
        case 'connected':
          if (lastStatus === 'reconnecting') {
            toast.success('Reconnected to server')
          } else if (lastStatus === 'disconnected' || lastStatus === 'error') {
            toast.success('Connected to server')
          }
          break
        case 'reconnecting':
          if (lastStatus === 'connected') {
            toast('Connection lost, reconnecting...', { id: 'reconnecting' })
          }
          break
        case 'error':
          toast.dismiss('reconnecting')
          toast.error('Connection error occurred')
          break
        case 'disconnected':
          toast.dismiss('reconnecting')
          if (lastStatus === 'connected' || lastStatus === 'reconnecting') {
            toast.warning('Disconnected from server')
          }
          break
      }

      lastStatusRef.current = currentStatus
    }
  }, [store.globalStatus, enableToasts])

  // Auto-sync with backend
  useEffect(() => {
    if (!autoSync) return

    const startSync = () => {
      syncIntervalRef.current = setInterval(() => {
        if (store.isOnline && store.globalStatus === 'connected') {
          store.syncWithBackend().catch((error) => {
            console.error('Auto-sync failed:', error)
          })
        }
      }, syncInterval)
    }

    // Initial sync
    if (store.isOnline && store.globalStatus === 'connected') {
      store.syncWithBackend()
    }

    startSync()

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current)
      }
    }
  }, [autoSync, syncInterval, store.isOnline, store.globalStatus])

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && autoSync) {
        // Page became visible, sync immediately
        store.syncWithBackend()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [autoSync])

  // Context value
  const contextValue: RealtimeContextValue = {
    subscribe: store.subscribe,
    unsubscribe: store.unsubscribe,
    sendMessage: store.sendMessage,
    isOnline: store.isOnline,
    isConnected: store.globalStatus === 'connected'
  }

  return (
    <RealtimeContext.Provider value={contextValue}>
      {children}
    </RealtimeContext.Provider>
  )
}

export const useRealtime = (): RealtimeContextValue => {
  const context = useContext(RealtimeContext)
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider')
  }
  return context
}

// Hook for subscribing to specific channels with automatic cleanup
export const useRealtimeSubscription = <T = unknown>(
  channel: string,
  callback: (data: T) => void,
  deps: React.DependencyList = []
) => {
  const { subscribe } = useRealtime()
  const callbackRef = useRef(callback)

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    const wrappedCallback = (data: unknown) => {
      callbackRef.current(data as T)
    }

    const unsubscribe = subscribe(channel, wrappedCallback)
    return unsubscribe
  }, [channel, subscribe, ...deps])
}

// Hook for sending messages with error handling
export const useRealtimeMessage = () => {
  const { sendMessage, isConnected } = useRealtime()

  return {
    send: (channel: string, type: string, payload: unknown) => {
      if (!isConnected) {
        console.warn('Cannot send message while disconnected. Message will be queued.')
      }
      sendMessage(channel, type, payload)
    },
    isConnected
  }
}

export default RealtimeProvider